<template>
    <div class="sora-model-page relative xl:pt-[6rem] pt-[3rem]">
        <!-- 背景视频占位 -->
        <video class="absolute top-0 left-0 w-full h-screen xl:h-[1080px] object-cover z-0 pointer-events-none"
            :poster="heroPoster" :src="demoVideos.heroBackground" autoplay muted loop
            preload="metadata" playsinline webkit-playsinline></video>
        <div class="absolute top-0 left-0 w-full h-screen xl:h-[1080px] bg-black/20 pointer-events-none z-[1]"></div>
        <!-- Video Generator Section -->
        <section ref="generatorSection" class="video-generator-section relative z-10">
            <div class="container">
                <div class="generator-container">
                    <!-- Generator Header -->
                    <div class="generator-header">
                        <div class="generator-title">
                            <div class="page-name animate-float backdrop-blur-md">
                                <span class="flex h-1.5 w-1.5 relative">
                                    <span class="relative inline-flex rounded-full h-1.5 w-1.5 bg-brand-neon"></span>
                                    <span
                                        class="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-neon opacity-75"></span>
                                </span>
                                <span>
                                    {{
                                        t('common.model_online', {
                                            model: 'WeryAI Video 1.0',
                                        })
                                    }}
                                </span>
                            </div>
                            <h1>{{ t('weryaiVideo1.generatorTitle') }}</h1>
                            <p class="generator-description !text-white"
                                v-html="t('weryaiVideo1.generatorDescription')"></p>
                            <div class="rainbow-bar"></div>
                        </div>
                    </div>

                    <!-- Generator Interface -->
                    <!-- PC 端显示 NewVideoGenerator -->
                    <div class="hidden md:block">
                        <NewVideoGenerator :model-name="t('weryaiVideo1.modelName')" :tabs="tabs"
                            redirect-url="/ai-video" @generate="handleGenerate" model-key="weryai-video-1">
                            <template #model-logo>
                                <img src="https://static.weryai.com/growth/image/weryai_425ccdc06ae1516f99bb84fe273147e9.png"
                                    alt="WeryAI Video 1.0 Logo" class="w-5 h-5 rounded" />
                            </template>
                        </NewVideoGenerator>
                    </div>
                    <!-- 移动端显示 MobileGenerator -->
                    <div class="block md:hidden">
                        <MobileGenerator type="video" model-name="WeryAI Video 1.0" model-key="weryai-video-1"
                            default-tab="image" />
                    </div>
                </div>
            </div>
        </section>

        <!-- Core Features Section -->
        <section class="core-features-section relative z-10 !bg-transparent">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title mt-[2rem]">
                        {{ t('weryaiVideo1.coreFeaturesTitle') }}
                    </h2>
                </div>
                <div class="features-showcase-container grid grid-cols-1 xl:grid-cols-2 gap-[80px]">
                    <div v-for="feature in coreFeatures" :key="feature.title" class="feature-card">
                        <div class="flex flex-col">
                            <div class="feature-video">
                                <VideoPlayer
                                    :poster="feature.thumbnail_url"
                                    :src="feature.url"
                                    :title="feature.title"
                                    :description="feature.description"
                                />
                            </div>
                            <div class="feature-text !px-0">
                                <h3 class="feature-title">
                                    {{ feature.title }}
                                </h3>
                                <p class="feature-description">
                                    {{ feature.description }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- How to Use Section -->
        <section class="how-to-use-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ t('weryaiVideo1.howToUseTitle') }}
                    </h2>
                    <h3 class="text-text-adad text-base">
                        {{ t('common.three_step') }}
                    </h3>
                </div>
                <ol class="gap-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3">
                    <li v-for="step in howToUseStepsList" :key="step.title"
                        class="step-card flex flex-col items-center justify-center gap-5 min-h-[150px] md:min-h-[200px] lg:min-h-[270px] border border-white/10 rounded-[20px] p-5">
                        <img :src="step.icon" :alt="step.title" class="step-icon w-[96px] h-[96px]" />
                        <div class="step-content">
                            <h3 class="text-[20px] font-bold text-white">
                                {{ step.title }}
                            </h3>
                            <p v-html="step.description" class="text-sm text-text-adad"></p>
                        </div>
                    </li>
                </ol>
            </div>
        </section>

        <!-- Explore Other Models Section -->
        <section class="explore-models-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ t('weryaiVideo1.exploreModelsTitle') }}
                    </h2>
                </div>

                <div class="models-grid">
                    <NuxtLink to="/video/runway-ai-4-5" class="model-card !bg-[#22242D]">
                        <div class="model-icon">
                            <img src="https://static.weryai.com/growth/image/a65ca4242d4a0dc3f1b65e1d96471144.png"
                                alt="Runway Gen-4.5 AI video model icon" loading="lazy" />
                        </div>
                        <h3 class="model-card-title">
                            {{ t('weryaiVideo1.runwayTitle') }}
                        </h3>
                    </NuxtLink>

                    <NuxtLink to="/video/kling-2.6-pro" class="model-card !bg-[#22242D]">
                        <div class="model-icon">
                            <img src="https://static.weryai.com/growth/image/1fda745d93db271f6da3cedc576b515a.png"
                                alt="Kling 2.6 AI video model icon" loading="lazy" />
                        </div>
                        <h3 class="model-card-title">
                            {{ t('weryaiVideo1.klingTitle') }}
                        </h3>
                    </NuxtLink>

                    <NuxtLink to="/video/veo/veo-3-1" class="model-card !bg-[#22242D]">
                        <div class="model-icon">
                            <img src="https://static.weryai.com/growth/image/3ed8c9e1a1ec3b37133c608c292821cb.png"
                                alt="Google Veo 3.1 AI video model icon" loading="lazy" />
                        </div>
                        <h3 class="model-card-title">
                            {{ t('weryaiVideo1.veo31Title') }}
                        </h3>
                    </NuxtLink>

                    <NuxtLink to="/video/sora/sora-2" class="model-card !bg-[#22242D]">
                        <div class="model-icon">
                            <img src="https://static.weryai.com/growth/image/7ac1a1457b1e788829a2da1d178879bb.png"
                                alt="Sora 2 AI video model icon" loading="lazy" />
                        </div>
                        <h3 class="model-card-title">
                            {{ t('weryaiVideo1.soraTitle') }}
                        </h3>
                    </NuxtLink>
                </div>
            </div>
        </section>

        <!-- FAQ Section -->
        <section class="faq-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ t('weryaiVideo1.faqTitle') }}
                    </h2>
                </div>

                <div class="faq-list">
                    <div v-for="(faq, index) in faqData" :key="index" class="faq-item"
                        :class="{ active: activeFaqIndex === index }">
                        <div class="faq-question" @click="toggleFaq(index)">
                            <span class="faq-text">{{ faq.question }}</span>
                        </div>
                        <div class="faq-answer">
                            <p>{{ faq.answer }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- CTA Section -->
        <section class="cta-section">
            <div class="container">
                <div class="cta-content">
                    <!-- WeryAI Logo -->
                    <div class="cta-logo">
                        <div class="logo-icon logo-image">
                            <img src="https://static.weryai.com/growth/image/weryai_425ccdc06ae1516f99bb84fe273147e9.png"
                                alt="WeryAI Video 1.0 logo" class="logo-image" loading="lazy" />
                        </div>
                    </div>

                    <!-- Main Content -->
                    <div class="cta-header">
                        <h2 class="cta-title">
                            {{ t('weryaiVideo1.ctaTitle') }}
                        </h2>
                        <p class="cta-description">
                            {{ t('weryaiVideo1.ctaDescription') }}
                        </p>
                    </div>

                    <!-- CTA Button -->
                    <div class="cta-actions">
                        <button @click="scrollToGenerator" class="cta-primary-btn">
                            {{ t('weryaiVideo1.ctaButton') }}
                        </button>

                        <p class="cta-disclaimer">
                            {{ t('weryaiVideo1.ctaDisclaimer') }}
                        </p>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useHead, useRequestURL } from '#imports'
import { useI18n } from '~/composables/useI18n'
import { analyticsEvent } from '~/utils/log-event-func'
import { useMultilingualSeo } from '~/composables/useMultilingualSeo'
import { useHowToSeo } from '~/composables/useHowToSeo'
import { useFaqSeo } from '~/composables/useFaqSeo'
import { getResourceThumbnail } from '~/utils/common'
import MobileGenerator from '~/components/mobile/MobileGenerator.vue'
import { useSettingVideoSEO } from '~/composables/useSettingVideoSEO'

const { t } = useI18n()

const demoVideos = {
    heroBackground: 'https://static.weryai.com/cms/weryai_video_01_bg02.mp4',

    multiShot:
        'https://static.weryai.com/growth/video/weryai_e4c59fe83aa3641c0a72aa2aaf110d47.mp4',
    imageToVideo:
        'https://static.weryai.com/growth/video/weryai_8f697940e8b9e611eeb483b40ce75d55.mp4',
    fastRender:
        'https://static.weryai.com/growth/video/weryai_99214a230297a1f13d5773c33039e3a1.mp4',
    physics:
        'https://static.weryai.com/growth/video/weryai_034e209e53859eeb0f63abdd4bff4228.mp4',
} as const

// 移动端检测
const isMobile = ref(false)
const MOBILE_BREAKPOINT = 768

const checkIsMobile = () => {
    if (import.meta.client) {
        isMobile.value = window.innerWidth < MOBILE_BREAKPOINT
    }
}

// 防抖处理 resize 事件
let resizeTimer: ReturnType<typeof setTimeout> | null = null
const handleResize = () => {
    if (resizeTimer) clearTimeout(resizeTimer)
    resizeTimer = setTimeout(() => {
        checkIsMobile()
    }, 150)
}
const requestUrl = useRequestURL()
const requestUrlHref = requestUrl.href

definePageMeta({
    layout: 'website',
})

useMultilingualSeo({ pageType: 'weryai_video_1' })

const heroPoster = computed(() =>
    getResourceThumbnail(
        'https://static.weryai.com/growth/image/weryai_78f88f08342ef5b98fa90b32d37fb4b2.png'
    )
)

useHead({
    link: [
        {
            rel: 'preload',
            as: 'image',
            href: heroPoster,
        },
    ],
})

// Ref for generator section
const generatorSection = ref<HTMLElement | null>(null)

// Scroll to generator function
const scrollToGenerator = () => {
    if (generatorSection.value) {
        generatorSection.value.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        })
    }
}

// Structured Data for SEO (BreadcrumbList + SoftwareApplication)
useHead({
    script: [
        {
            type: 'application/ld+json',
            innerHTML: computed(() =>
                JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'BreadcrumbList',
                    itemListElement: [
                        {
                            '@type': 'ListItem',
                            position: 1,
                            name: 'Home',
                            item: 'https://www.weryai.com.cn/',
                        },
                        {
                            '@type': 'ListItem',
                            position: 2,
                            name: t('weryaiVideo1.generatorTitle'),
                            item: requestUrlHref,
                        },
                    ],
                })
            ),
        },
        {
            type: 'application/ld+json',
            innerHTML: computed(() =>
                JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'SoftwareApplication',
                    name: t('weryaiVideo1.generatorTitle'),
                    description: t('weryaiVideo1.pageDescription'),
                    applicationCategory: 'MultimediaApplication',
                    operatingSystem: 'Web',
                    offers: {
                        '@type': 'Offer',
                        price: '0',
                        priceCurrency: 'USD',
                    },
                    provider: {
                        '@type': 'Organization',
                        name: '微鲤AI',
                        url: 'https://www.weryai.com.cn',
                    },
                    featureList: [
                        t('weryaiVideo1.multiShotTitle'),
                        t('weryaiVideo1.imageToVideoTitle'),
                        t('weryaiVideo1.fastRenderTitle'),
                        t('weryaiVideo1.physicsTitle'),
                    ],
                })
            ),
        },
    ],
})

// Tabs configuration
const tabs = computed(() => [
    {
        name: t('weryaiVideo1.tabImageToVideo'),
        external: false,
        value: 'image',
    },
    { name: t('weryaiVideo1.tabTextToVideo'), external: false, value: 'text' },
])

// Methods
const handleGenerate = (data: {
    type: 'image' | 'text'
    image?: File
    prompt?: string
}) => {
    // Handle generation logic here
}

// Page Mounted
onMounted(() => {
    // Page view analytics tracking
    analyticsEvent('page_view_weryai_video_1_0')
    analyticsEvent('view_model_page_common', {
        model_name: 'weryai-video-1',
        type: 'video',
    })

    // 初始化移动端检测
    checkIsMobile()
    window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
    // 清理 resize 事件监听器
    window.removeEventListener('resize', handleResize)
    if (resizeTimer) clearTimeout(resizeTimer)
})

// FAQ Data
const faqData = computed(() => [
    {
        question: t('weryaiVideo1.faq1Question'),
        answer: t('weryaiVideo1.faq1Answer'),
    },
    {
        question: t('weryaiVideo1.faq2Question'),
        answer: t('weryaiVideo1.faq2Answer'),
    },
    {
        question: t('weryaiVideo1.faq3Question'),
        answer: t('weryaiVideo1.faq3Answer'),
    },
    {
        question: t('weryaiVideo1.faq4Question'),
        answer: t('weryaiVideo1.faq4Answer'),
    },
    {
        question: t('weryaiVideo1.faq5Question'),
        answer: t('weryaiVideo1.faq5Answer'),
    },
])

// FAQ SEO - 注入 FAQPage 结构化数据
useFaqSeo(faqData)

// FAQ Interactive Functions
const activeFaqIndex = ref<number | null>(null)

const toggleFaq = (index: number) => {
    if (activeFaqIndex.value === index) {
        activeFaqIndex.value = null
    } else {
        activeFaqIndex.value = index
    }
}

// Core Features
const coreFeatures = computed(() => [
    {
        title: t('weryaiVideo1.multiShotTitle'),
        description: t('weryaiVideo1.multiShotDescription'),
        url: demoVideos.multiShot,
        thumbnail_url: 'https://static.weryai.com/cms/weryai_video_01_w.webp',
    },
    {
        title: t('weryaiVideo1.imageToVideoTitle'),
        description: t('weryaiVideo1.imageToVideoDescription'),
        url: demoVideos.imageToVideo,
        thumbnail_url: 'https://static.weryai.com/cms/weryai_video_02_w.webp',
    },
    {
        title: t('weryaiVideo1.fastRenderTitle'),
        description: t('weryaiVideo1.fastRenderDescription'),
        url: demoVideos.fastRender,
        thumbnail_url: 'https://static.weryai.com/cms/weryai_video_03_w.webp',
    },
    {
        title: t('weryaiVideo1.physicsTitle'),
        description: t('weryaiVideo1.physicsDescription'),
        url: demoVideos.physics,
        thumbnail_url: 'https://static.weryai.com/cms/weryai_video_04_w.webp',
    },
])

// HowTo SEO - 注入结构化数据
const howToUseSteps = computed(() => [
    {
        title: t('weryaiVideo1.step1Title'),
        description: t('weryaiVideo1.step1Description'),
    },
    {
        title: t('weryaiVideo1.step2Title'),
        description: t('weryaiVideo1.step2Description'),
    },
    {
        title: t('weryaiVideo1.step3Title'),
        description: t('weryaiVideo1.step3Description'),
    },
])

useHowToSeo(
    computed(() => t('weryaiVideo1.howToUseTitle')),
    howToUseSteps
)

// HowTo Steps for display
const howToUseStepsList = computed(() => [
    {
        title: t('weryaiVideo1.step1Title'),
        description: t('weryaiVideo1.step1Description'),
        icon: 'https://static.weryai.com/cms/weryai_d235e92179a57a96bd38dc29336cc6bf.webp',
    },
    {
        title: t('weryaiVideo1.step2Title'),
        description: t('weryaiVideo1.step2Description'),
        icon: 'https://static.weryai.com/cms/weryai_3585be709806892f4d0a51e8b53d4c20.webp',
    },
    {
        title: t('weryaiVideo1.step3Title'),
        description: t('weryaiVideo1.step3Description'),
        icon: 'https://static.weryai.com/cms/weryai_e637ad7b5e3ed566325b8a9be194bfd2.webp',
    },
])

useSettingVideoSEO({
    key: 'weryaiVideo1-videoobject',
    title: t('weryaiVideo1.generatorTitle'),
    description: t('weryaiVideo1.generatorDescription'),
    contentUrl: demoVideos.heroBackground,
    thumbnailUrl: heroPoster.value,
})
</script>

<style lang="less" scoped>
@import '~/assets/css/howToUse.css';

/* 移动端样式覆盖 */
@media (max-width: 768px) {
    .container {
        padding: 0 16px !important;
    }

    .generator-container {
        padding: 0 !important;
        max-width: 100% !important;
    }

    /* 功能特性卡片样式 */
    .feature-card {
        border-radius: 16px;
        border: 1px solid rgba(255, 255, 255, 0.05);
        background: #120E11;
        padding: 16px;
        overflow: hidden;
    }

    .features-showcase-container {
        gap: 16px !important;
    }

    /* 如何使用部分 - 移动端横向布局 */
    .how-to-use-section ol {
        gap: 12px !important;
    }

    .how-to-use-section ol li.step-card {
        flex-direction: row !important;
        align-items: center !important;
        justify-content: flex-start !important;
        gap: 16px !important;
        min-height: auto !important;
        padding: 16px !important;
        border-radius: 16px !important;
    }

    /* 第1个和第2个卡片样式 */
    .how-to-use-section ol li.step-card:nth-child(1),
    .how-to-use-section ol li.step-card:nth-child(2) {
        border: 1px solid rgba(255, 255, 255, 0.10) !important;
        background: rgba(255, 255, 255, 0.05) !important;
        backdrop-filter: blur(6px);
    }

    /* 第3个卡片样式 */
    .how-to-use-section ol li.step-card:nth-child(3) {
        border: 1px solid rgba(255, 255, 255, 0.10) !important;
        background: rgba(153, 247, 104, 0.05) !important;
        backdrop-filter: blur(6px);
    }

    .how-to-use-section .step-icon {
        width: 64px !important;
        height: 64px !important;
        flex-shrink: 0;
    }

    .how-to-use-section .step-content {
        flex: 1;
        text-align: left;
    }

    .how-to-use-section .step-content h3 {
        font-size: 16px !important;
        text-align: left !important;
        margin-bottom: 4px;
    }

    .how-to-use-section .step-content p {
        text-align: left !important;
        margin: 0;
    }
}
</style>
